import * as releasesModel from "./model/releasesModel";
import * as roadmapModel from "./model/roadmapModel";
import * as feedbackModel from "./model/feedbackModel";
import * as audioModel from "./model/audioModel";
import * as userModel from "./model/userModel";
import * as metricsModel from "./model/metricsModel";
import * as demoModel from "./model/demoModel";
import * as profileModel from "./model/profileModel";
import * as demoPlayHistoryModel from "./model/demoPlayHistoryModel";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { logger } from "./LogUtils";
import { FeatureUpdate, FeatureCreate, FeatureGet } from "./types/feature";
dayjs.extend(utc);

export const DEMO_PORTAL_API_URL = `${import.meta.env.REACT_APP_API_BASE}/api/demoportalapi`;
export const REGISTER_PARTNER_API_URL = `${import.meta.env.REACT_APP_API_BASE}/api/registerpartner`;

/**
 * Creates a new feature
 */
export async function createFeature(
  feature: FeatureCreate
): Promise<{ featureId: string }> {
  try {
    const options = {
      withCredentials: true,
    };

    const body = {
      action: "createFeature",
      feature: feature,
    };

    logger.info(
      `Model.createFeature() creating feature with: ${JSON.stringify(
        body,
        null,
        2
      )}`
    );

    const response = await axios.post(DEMO_PORTAL_API_URL, body, options);

    if (response.data?.success !== true) {
      throw new Error(response.data?.cause);
    }

    logger.info(
      `Model.createFeature() got response: ${JSON.stringify(
        response.data,
        null,
        2
      )}`
    );

    return response.data;
  } catch (error) {
    logger.error("Model.createFeature() failed to create feature", error);
    throw error;
  }
}

/**
 * Saves a feature
 */
export async function saveFeature(feature: FeatureUpdate): Promise<FeatureGet> {
  try {
    const options = {
      withCredentials: true,
    };

    const body = {
      action: "saveFeature",
      feature: feature,
    };

    logger.info(
      `Model.saveFeature() saving a feature with: ${JSON.stringify(
        body,
        null,
        2
      )}`
    );

    const response = await axios.post(DEMO_PORTAL_API_URL, body, options);

    if (response.data?.success !== true) {
      throw new Error(response.data?.cause);
    }

    logger.info(
      `Model.saveFeature() got response: ${JSON.stringify(
        response.data,
        null,
        2
      )}`
    );

    return response.data;
  } catch (error) {
    logger.error("Model.saveFeature() failed to save feature", error);
    throw error;
  }
}

/**
 * Fetches a feature by feature id used for editing the feature
 */
export async function getFeature(featureId: string): Promise<FeatureGet> {
  try {
    const options = {
      withCredentials: true,
    };

    const body = {
      action: "getFeature",
      featureId: featureId,
    };

    logger.info(
      `Model.getFeature() getting feature with: ${JSON.stringify(
        body,
        null,
        2
      )}`
    );

    const response = await axios.post(DEMO_PORTAL_API_URL, body, options);

    if (response.data?.success !== true) {
      throw new Error(response.data?.cause);
    }

    logger.info(
      `Model.getFeature() got response: ${JSON.stringify(
        response.data,
        null,
        2
      )}`
    );

    const feature = response.data.feature;

    return feature;
  } catch (error) {
    logger.error("Model.getFeature() failed to get feature", error);
    throw error;
  }
}

/**
 * Lists all features
 */
export async function listFeatures(
  enabledOnly: boolean = true
): Promise<FeatureGet[]> {
  try {
    const options = {
      withCredentials: true,
    };

    const body = {
      action: "listFeatures",
    };

    logger.info(
      `Model.listFeatures() listing features with: ${JSON.stringify(
        body,
        null,
        2
      )}`
    );

    const response = await axios.post(DEMO_PORTAL_API_URL, body, options);

    if (response.data?.success !== true) {
      throw new Error(response.data?.cause);
    }

    let features: FeatureGet[] = response.data.features;

    if (enabledOnly) {
      features = features.filter((feature) => feature.enabled === true);
    }

    return features;
  } catch (error) {
    logger.error("Model.listFeatures() failed to list features", error);
    throw error;
  }
}

/**
 * Delete features
 */
export async function deleteFeatures(featureIds: string[]): Promise<void> {
  try {
    const options = {
      withCredentials: true,
    };

    const body = {
      action: "deleteFeatures",
      featureIds: featureIds,
    };

    logger.info(
      `Model.deleteFeature() deleting features with: ${JSON.stringify(
        body,
        null,
        2
      )}`
    );

    const response = await axios.post(DEMO_PORTAL_API_URL, body, options);

    if (response.data?.success !== true) {
      throw new Error(response.data?.cause);
    }

    logger.info(
      `Model.deleteFeature() got response: ${JSON.stringify(
        response.data,
        null,
        2
      )}`
    );

    return null;
  } catch (error) {
    logger.error("Model.deleteFeature() failed to delete features", error);
    throw error;
  }
}

export {
  releasesModel,
  roadmapModel,
  feedbackModel,
  audioModel,
  userModel,
  metricsModel,
  demoModel,
  profileModel,
  demoPlayHistoryModel,
};
